import { useCustomToast } from '@elkaso-app/web-design';
import http from 'apis/config/http-service';
import { useQueryWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import { useIsLoggedIn } from 'hooks/use-is-logged-in';
import { UseQueryOptions, UseQueryResult } from 'react-query';
import type { ProductBrand } from './types';

export const productBrandsEndpoint = 'ums/v1/cms/product-brands';

interface ResponseData {
  data: ProductBrand[];
}

type TData = ResponseData;
type TError = AxiosError;
type TQueryKey = [string];
type Options = Omit<UseQueryOptions<unknown, TError, TData, TQueryKey>, 'queryKey' | 'queryFn'>;
type ReturnType = UseQueryResult<TData, TError>;

/**
 * GET ums/v1/cms/product-brands
 * CMS management surface — all brands, active and inactive.
 * No staleTime: the brands management screen always wants fresh data.
 */
export function useGetProductBrandsApi(options?: Options): ReturnType {
  const toast = useCustomToast();
  const isLoggedIn = useIsLoggedIn();
  // Destructure to compose caller enabled with built-in guards (never replace)
  const { enabled: callerEnabled, ...restOptions } = options ?? {};

  const queryFn = async () => {
    try {
      const { data } = await http.get(productBrandsEndpoint);

      return data;
    } catch (error: any) {
      if (!error.response?.status || !error.response?.data?.message) throw error;

      toast({
        description: error.response.data.message,
        status: 'error',
      });

      throw error;
    }
  };

  return useQueryWrapper([productBrandsEndpoint], queryFn, {
    enabled: (callerEnabled ?? true) && isLoggedIn,
    staleTime: 0,
    retry: false,
    ...restOptions,
  });
}
